// Consultas no app (aba Perfil → "Consultas", pedido 2026-10-28: "tela de
// consultas bem legível, visual bem visível para o app PWA"). Mesmas regras
// da tela do computador (routes/consultas.tsx — CPF, finalidade, LGPD,
// crédito PIX, histórico que reabre sem cobrar), mas em tela cheia de
// celular: letra grande, cartões claros que seguem o tema do app e o
// resultado numa coluna só, do mais importante (antecedentes/processos)
// para o resto.
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Car,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Gavel,
  IdCard,
  Info,
  Landmark,
  Loader2,
  Lock,
  Mail,
  MapPin,
  Network,
  Phone,
  QrCode,
  Search,
  ShieldAlert,
  ShieldCheck,
  User,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useConsultationAccess,
  useConsultationCredits,
  useConsultationHistory,
  useConsultationSettings,
} from "@/hooks/queries/consultations";
import { consultationsService, type Consultation } from "@/services/gpsdrive-consultations.service";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import { ConsultationPrintReport } from "@/components/consultas/consultation-report";
import { legalOf } from "@/components/consultas/legal-checks";
import { publicDataOf } from "@/components/consultas/public-data-panel";
import type { Profile, ProfileList, ProfileRecord } from "@/components/consultas/profile";
import { LicensePlate } from "@/components/license-plate";
import {
  PURPOSES,
  PixCreditDialog,
  consultationProfile,
  errMsg,
  extractData,
  fmtBrl,
  maskCpf,
  runErrMsg,
  validCpf,
} from "@/components/consultas/shared";

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

type View = { kind: "home" } | { kind: "loading" } | { kind: "result"; consultation: Consultation };

export function MobileConsultationsScreen({ onClose }: { onClose: () => void }) {
  const { data: access, isLoading } = useConsultationAccess();
  const [view, setView] = useState<View>({ kind: "home" });
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const openFromHistory = useCallback(
    async (id: string) => {
      setError(null);
      setView({ kind: "loading" });
      try {
        const c = await qc.fetchQuery({
          queryKey: ["consultations", "one", id],
          queryFn: () => consultationsService.get(id),
        });
        setView({ kind: "result", consultation: c });
      } catch (err) {
        setError(errMsg(err));
        setView({ kind: "home" });
      }
    },
    [qc],
  );

  const back = view.kind === "result" ? () => setView({ kind: "home" }) : onClose;

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <header
        className="shrink-0 bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 px-4 pb-4 text-white shadow-md"
        style={{ paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={back}
            aria-label="Voltar"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/20 active:bg-white/30"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl font-extrabold leading-tight">
              {view.kind === "result" ? "Resultado da consulta" : "Consultas"}
            </h1>
            <p className="text-[13px] text-emerald-50/90">Antecedentes, CPF e processos</p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-10 pt-4">
        {isLoading ? (
          <Centered>
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </Centered>
        ) : !access?.view ? (
          <Card className="mt-6 flex flex-col items-center gap-3 p-6 text-center">
            <Lock className="h-10 w-10 text-emerald-600" />
            <div className="text-lg font-bold">Sem acesso a Consultas</div>
            <p className="text-[15px] text-slate-600 dark:text-slate-400">
              Peça a um administrador para liberar a permissão de Consultas.
            </p>
          </Card>
        ) : view.kind === "loading" ? (
          <LoadingCard />
        ) : view.kind === "result" ? (
          <ResultView consultation={view.consultation} />
        ) : (
          <Home
            error={error}
            onError={setError}
            onRunning={() => setView({ kind: "loading" })}
            onResult={(c) => setView({ kind: "result", consultation: c })}
            onFail={() => setView({ kind: "home" })}
            onOpen={openFromHistory}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Peças visuais

function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900",
        className,
      )}
    >
      {children}
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="flex min-h-[40vh] items-center justify-center">{children}</div>;
}

function SectionTitle({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) {
  return (
    <div className="mb-2 mt-6 flex items-center gap-2 px-1 text-[13px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
      <Icon className="h-4 w-4" /> {children}
    </div>
  );
}

function LoadingCard() {
  return (
    <Card className="mt-4 flex flex-col items-center gap-4 px-6 py-12 text-center">
      <Loader2 className="h-12 w-12 animate-spin text-emerald-600" />
      <div className="text-lg font-bold">Consultando…</div>
      <p className="text-[15px] text-slate-600 dark:text-slate-400">
        Pode levar até 2 minutos. Não feche esta tela.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tela inicial: saldo, formulário e histórico

function Home({
  error,
  onError,
  onRunning,
  onResult,
  onFail,
  onOpen,
}: {
  error: string | null;
  onError: (m: string | null) => void;
  onRunning: () => void;
  onResult: (c: Consultation) => void;
  onFail: () => void;
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const { data: settings, isLoading: settingsLoading } = useConsultationSettings();
  const [cpf, setCpf] = useState("");
  const [purpose, setPurpose] = useState(PURPOSES[0]);
  const [purposeOther, setPurposeOther] = useState("");
  const [agree, setAgree] = useState(false);
  const [pixOpen, setPixOpen] = useState(false);
  const { data: credits } = useConsultationCredits(true, pixOpen);
  const paid = !!credits?.required;
  const price = credits?.price ?? 10;
  const noCredit = paid && (credits?.balance ?? 0) < price;
  const hasToken = !!settings?.hasToken;

  const run = async () => {
    if (!validCpf(cpf)) return onError("CPF inválido. Confira os 11 números.");
    const finalPurpose = purpose === "Outra" ? purposeOther.trim() : purpose;
    if (finalPurpose.length < 3) return onError("Descreva a finalidade da consulta.");
    if (!agree) return onError("Marque a declaração da LGPD antes de consultar.");
    onError(null);
    if (noCredit) return setPixOpen(true);
    onRunning();
    try {
      const r = await consultationsService.run({ type: "completa", cpf, purpose: finalPurpose });
      onResult(r);
      qc.invalidateQueries({ queryKey: ["consultations", "list"] });
      qc.invalidateQueries({ queryKey: ["consultations", "settings"] });
      qc.invalidateQueries({ queryKey: ["consultations", "credits"] });
    } catch (err) {
      onError(runErrMsg(err));
      onFail();
      qc.invalidateQueries({ queryKey: ["consultations", "credits"] });
      qc.invalidateQueries({ queryKey: ["consultations", "list"] });
      // a consulta pode ainda estar terminando no servidor
      setTimeout(() => qc.invalidateQueries({ queryKey: ["consultations", "list"] }), 45_000);
    }
  };

  return (
    <>
      {!settingsLoading && !hasToken && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-[15px] text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          As consultas ainda não foram ativadas para a sua empresa. Fale com o suporte.
        </div>
      )}

      {paid && credits && (
        <Card className="mb-4 flex items-center gap-3 p-4">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
            <Wallet className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-slate-500 dark:text-slate-400">Seu saldo</div>
            <div className="text-2xl font-extrabold leading-tight">{fmtBrl(credits.balance)}</div>
            <div className="text-[13px] text-slate-500 dark:text-slate-400">
              {Math.floor(credits.balance / credits.price)} consulta(s) · {fmtBrl(credits.price)}{" "}
              cada
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPixOpen(true)}
            className="flex h-12 shrink-0 flex-col items-center justify-center rounded-xl border-2 border-emerald-600 px-3 text-[12px] font-bold text-emerald-700 active:bg-emerald-50 dark:text-emerald-300 dark:active:bg-emerald-500/10"
          >
            <QrCode className="h-5 w-5" />
            PIX
          </button>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex items-start gap-3">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-emerald-600 text-white">
            <ShieldCheck className="h-6 w-6" />
          </span>
          <div>
            <div className="text-lg font-extrabold leading-tight">Consulta completa</div>
            <p className="mt-0.5 text-[14px] leading-snug text-slate-600 dark:text-slate-400">
              Antecedentes criminais, CPF completo, processos, mandados, protestos, dívida ativa e
              dados públicos — tudo numa consulta só.
            </p>
          </div>
        </div>

        <label className="mt-5 block">
          <span className="mb-1.5 block text-[15px] font-semibold">CPF da pessoa</span>
          <div className="relative">
            <IdCard className="pointer-events-none absolute left-4 top-1/2 h-6 w-6 -translate-y-1/2 text-slate-400" />
            <input
              value={cpf}
              onChange={(e) => setCpf(maskCpf(e.target.value))}
              inputMode="numeric"
              autoComplete="off"
              placeholder="000.000.000-00"
              className="h-16 w-full rounded-xl border-2 border-slate-300 bg-white pl-14 pr-4 font-mono text-[22px] font-semibold tracking-wider outline-none focus:border-emerald-600 dark:border-slate-700 dark:bg-slate-950"
            />
          </div>
        </label>

        <label className="mt-4 block">
          <span className="mb-1.5 block text-[15px] font-semibold">Para que é a consulta?</span>
          <select
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            className="h-14 w-full rounded-xl border-2 border-slate-300 bg-white px-3 text-[16px] outline-none focus:border-emerald-600 dark:border-slate-700 dark:bg-slate-950"
          >
            {PURPOSES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
        {purpose === "Outra" && (
          <input
            value={purposeOther}
            onChange={(e) => setPurposeOther(e.target.value)}
            placeholder="Descreva a finalidade"
            className="mt-2 h-14 w-full rounded-xl border-2 border-slate-300 bg-white px-4 text-[16px] outline-none focus:border-emerald-600 dark:border-slate-700 dark:bg-slate-950"
          />
        )}

        <label className="mt-4 flex items-start gap-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/60">
          <input
            type="checkbox"
            checked={agree}
            onChange={(e) => setAgree(e.target.checked)}
            className="mt-0.5 h-6 w-6 shrink-0 accent-emerald-600"
          />
          <span className="text-[14px] leading-snug text-slate-700 dark:text-slate-300">
            Declaro que tenho base legal (LGPD) para esta consulta e que ela será usada só para a
            finalidade informada.
          </span>
        </label>

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-300 bg-rose-50 p-3 text-[15px] font-medium text-rose-800 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={run}
          disabled={!hasToken}
          className="mt-5 flex h-16 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 text-[18px] font-extrabold text-white shadow-lg shadow-emerald-600/30 active:bg-emerald-700 disabled:opacity-50"
        >
          <Search className="h-6 w-6" />
          {noCredit ? `PAGAR ${fmtBrl(price)} E CONSULTAR` : "CONSULTAR"}
        </button>
        <p className="mt-3 text-center text-[13px] text-slate-500 dark:text-slate-400">
          Quem consultou, data, hora e finalidade ficam registrados.
        </p>
      </Card>

      <HistoryList onOpen={onOpen} />

      {paid && credits && (
        <PixCreditDialog
          open={pixOpen}
          balance={credits.balance}
          price={credits.price}
          needsBillingDocument={!!credits.needsBillingDocument}
          onClose={() => setPixOpen(false)}
        />
      )}
    </>
  );
}

function HistoryList({ onOpen }: { onOpen: (id: string) => void }) {
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const { data, isLoading } = useConsultationHistory(1, pageSize, search.replace(/\D/g, ""));
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  return (
    <>
      <SectionTitle icon={Clock}>Consultas feitas</SectionTitle>
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={(e) => setSearch(maskCpf(e.target.value))}
          inputMode="numeric"
          placeholder="Buscar por CPF"
          className="h-12 w-full rounded-xl border border-slate-300 bg-white pl-12 pr-3 text-[16px] outline-none focus:border-emerald-600 dark:border-slate-700 dark:bg-slate-900"
        />
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        </div>
      ) : !items.length ? (
        <Card className="px-4 py-8 text-center text-[15px] text-slate-500 dark:text-slate-400">
          Nenhuma consulta feita ainda.
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((c) => {
            const ok = c.status === "sucesso";
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onOpen(c.id)}
                className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm active:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:active:bg-slate-800"
              >
                <span
                  className={cn(
                    "grid h-11 w-11 shrink-0 place-items-center rounded-full",
                    ok
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                      : "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
                  )}
                >
                  {ok ? <CheckCircle2 className="h-6 w-6" /> : <AlertTriangle className="h-6 w-6" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-[17px] font-bold">{maskCpf(c.document)}</span>
                  <span className="block truncate text-[13px] text-slate-500 dark:text-slate-400">
                    {fmtDate(c.createdAt)}
                    {c.userName ? ` · ${c.userName}` : ""}
                  </span>
                  {!ok && (
                    <span className="block text-[13px] font-semibold text-rose-600 dark:text-rose-300">
                      Não concluída
                    </span>
                  )}
                </span>
                <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" />
              </button>
            );
          })}
          {total > items.length && (
            <button
              type="button"
              onClick={() => setPageSize((n) => n + 10)}
              className="h-12 w-full rounded-xl border border-slate-300 bg-white text-[15px] font-semibold text-emerald-700 dark:border-slate-700 dark:bg-slate-900 dark:text-emerald-300"
            >
              Ver mais ({total - items.length})
            </button>
          )}
          <p className="pt-1 text-center text-[13px] text-slate-500 dark:text-slate-400">
            Abrir uma consulta antiga não gasta crédito.
          </p>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Resultado

const RECORD_TONE: Record<ProfileRecord["status"], { box: string; badge: string; label: string }> = {
  nada_consta: {
    box: "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10",
    badge: "bg-emerald-600 text-white",
    label: "NADA CONSTA",
  },
  consta: {
    box: "border-rose-300 bg-rose-50 dark:border-rose-500/40 dark:bg-rose-500/10",
    badge: "bg-rose-600 text-white",
    label: "CONSTA",
  },
  info: {
    box: "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50",
    badge: "bg-slate-500 text-white",
    label: "INFORMAÇÃO",
  },
};

function ResultView({ consultation: c }: { consultation: Consultation }) {
  const [printing, setPrinting] = useState(false);
  const donePrinting = useCallback(() => setPrinting(false), []);
  const legal = useMemo(() => legalOf(c.result), [c]);
  const publicData = useMemo(() => publicDataOf(c.result), [c]);
  const profile = useMemo(() => consultationProfile(c, legal), [c, legal]);
  const data = extractData(c.result);
  const ok = c.status === "sucesso";
  const empty =
    data == null ||
    (typeof data === "object" && !Array.isArray(data) && Object.keys(data as object).length === 0);
  const cpf = maskCpf(profile.cpf ?? c.document);
  const pdfHref = c.pdfUrl ? (c.pdfUrl.startsWith("/") ? resolveGpsDriveUrl(c.pdfUrl) : c.pdfUrl) : null;

  if (!ok || empty) {
    return (
      <Card className="mt-2 flex flex-col items-center gap-3 p-6 text-center">
        <AlertTriangle className={cn("h-12 w-12", ok ? "text-slate-400" : "text-rose-500")} />
        <div className="text-lg font-bold">
          {ok ? "Nenhum dado encontrado" : "Não foi possível concluir a consulta"}
        </div>
        <div className="font-mono text-[17px]">{cpf}</div>
        <p className="text-[15px] text-slate-600 dark:text-slate-400">
          {ok ? "A fonte não retornou dados para este CPF." : (c.message ?? "Tente novamente.")}
        </p>
      </Card>
    );
  }

  const consta = profile.records.filter((r) => r.status === "consta").length;
  const covered = (l: ProfileList) => !profile.notCovered?.[l];

  return (
    <>
      <ConsultationPrintReport
        open={printing}
        onDone={donePrinting}
        fileName={`Consulta ${profile.name ?? ""} ${cpf}`.replace(/\s+/g, " ").trim()}
        meta={{
          id: c.id,
          typeLabel: "Consulta completa",
          createdAt: c.createdAt,
          userName: c.userName,
          purpose: c.purpose,
          cpf,
        }}
        profile={profile}
        publicData={publicData}
        legal={legal}
      />

      {/* Identificação */}
      <Card className="p-5">
        <div className="flex items-center gap-4">
          {profile.photo ? (
            <img
              src={profile.photo}
              alt=""
              className="h-16 w-16 shrink-0 rounded-full object-cover"
            />
          ) : (
            <span className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800">
              <User className="h-8 w-8" />
            </span>
          )}
          <div className="min-w-0">
            <div className="text-[21px] font-extrabold leading-tight">{profile.name ?? "Nome não informado"}</div>
            <div className="mt-1 font-mono text-[17px] font-semibold text-slate-600 dark:text-slate-300">
              {cpf}
            </div>
          </div>
        </div>
        <dl className="mt-4 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
          <Field label="Nascimento" value={profile.birthDate} />
          <Field label="Situação do CPF" value={profile.status} />
          <Field label="Mãe" value={profile.mother} />
          <Field label="Pai" value={profile.father} />
          <Field label="RG" value={profile.rg} />
          <Field label="Naturalidade" value={profile.birthplace} />
          <Field label="Sexo" value={profile.gender} />
          {profile.extras.map((x) => (
            <Field key={x.label} label={x.label} value={x.value} />
          ))}
        </dl>
        <div className="mt-4 text-[13px] text-slate-500 dark:text-slate-400">
          Consultado em {fmtDate(c.createdAt)}
          {c.userName ? ` por ${c.userName}` : ""}
          {c.purpose ? ` · ${c.purpose}` : ""}
        </div>
      </Card>

      {/* Resumo grande: o que mais importa */}
      <div
        className={cn(
          "mt-4 flex items-center gap-3 rounded-2xl border-2 p-4",
          consta
            ? "border-rose-400 bg-rose-50 text-rose-900 dark:bg-rose-500/10 dark:text-rose-100"
            : "border-emerald-400 bg-emerald-50 text-emerald-900 dark:bg-emerald-500/10 dark:text-emerald-100",
        )}
      >
        {consta ? <ShieldAlert className="h-10 w-10 shrink-0" /> : <ShieldCheck className="h-10 w-10 shrink-0" />}
        <div>
          <div className="text-[19px] font-extrabold leading-tight">
            {consta ? `${consta} apontamento(s) encontrado(s)` : "Nenhum apontamento"}
          </div>
          <div className="text-[14px] opacity-80">
            {profile.processes.length} processo(s) · {profile.records.length} base(s) verificada(s)
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setPrinting(true)}
        disabled={printing}
        className="mt-4 flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 text-[16px] font-bold text-white active:bg-emerald-700 disabled:opacity-60"
      >
        <FileText className="h-5 w-5" /> Baixar relatório em PDF
      </button>

      {/* Antecedentes e restrições */}
      {covered("records") && (
        <>
          <SectionTitle icon={ShieldAlert}>Antecedentes e restrições</SectionTitle>
          {profile.records.length ? (
            <div className="space-y-2">
              {profile.records.map((r, i) => {
                const t = RECORD_TONE[r.status];
                return (
                  <div key={i} className={cn("rounded-2xl border p-4", t.box)}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-[16px] font-bold leading-snug">{r.title}</div>
                      <span className={cn("shrink-0 rounded-full px-2.5 py-1 text-[12px] font-extrabold", t.badge)}>
                        {t.label}
                      </span>
                    </div>
                    {r.detail && (
                      <p className="mt-1.5 text-[14px] leading-snug text-slate-700 dark:text-slate-300">
                        {r.detail}
                      </p>
                    )}
                    {r.source && (
                      <div className="mt-1 text-[12px] text-slate-500 dark:text-slate-400">Fonte: {r.source}</div>
                    )}
                    {!r.noPdf && pdfHref && (
                      <a
                        href={pdfHref}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-[14px] font-semibold text-emerald-700 dark:text-emerald-300"
                      >
                        <FileText className="h-4 w-4" /> Ver certidão
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyCard>Nenhuma base de antecedentes retornou dados.</EmptyCard>
          )}
        </>
      )}

      {covered("processes") && (
        <Collapsible icon={Gavel} title="Processos" count={profile.processes.length} defaultOpen>
          {profile.processes.map((p, i) => (
            <Row key={i}>
              <div className="break-all font-mono text-[15px] font-bold">{p.number}</div>
              {p.subject && <div className="mt-0.5 text-[15px]">{p.subject}</div>}
              <div className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                {[p.court, p.status, p.date].filter(Boolean).join(" · ")}
              </div>
            </Row>
          ))}
        </Collapsible>
      )}

      {covered("addresses") && (
        <Collapsible icon={MapPin} title="Endereços" count={profile.addresses.length}>
          {profile.addresses.map((a, i) => {
            const full = [a.line, a.district, a.city, a.zip].filter(Boolean).join(", ");
            return (
              <Row key={i}>
                <div className="text-[15px] font-semibold leading-snug">{a.line}</div>
                <div className="text-[14px] text-slate-600 dark:text-slate-400">
                  {[a.district, a.city, a.zip].filter(Boolean).join(" · ")}
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(full)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-1 inline-flex items-center gap-1 text-[14px] font-semibold text-emerald-700 dark:text-emerald-300"
                >
                  <MapPin className="h-4 w-4" /> Ver no mapa
                </a>
              </Row>
            );
          })}
        </Collapsible>
      )}

      {covered("phones") && (
        <Collapsible icon={Phone} title="Telefones" count={profile.phones.length}>
          {profile.phones.map((p, i) => (
            <Row key={i}>
              <a
                href={`tel:${p.number.replace(/\D/g, "")}`}
                className="flex items-center justify-between gap-2"
              >
                <span>
                  <span className="block font-mono text-[17px] font-bold">{p.number}</span>
                  <span className="block text-[13px] text-slate-500 dark:text-slate-400">
                    {[p.kind, p.carrier].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <span className="grid h-10 w-10 place-items-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                  <Phone className="h-5 w-5" />
                </span>
              </a>
            </Row>
          ))}
        </Collapsible>
      )}

      {covered("emails") && (
        <Collapsible icon={Mail} title="E-mails" count={profile.emails.length}>
          {profile.emails.map((e, i) => (
            <Row key={i}>
              <a href={`mailto:${e.address}`} className="break-all text-[15px] font-semibold text-emerald-700 dark:text-emerald-300">
                {e.address}
              </a>
            </Row>
          ))}
        </Collapsible>
      )}

      {covered("vehicles") && (
        <Collapsible icon={Car} title="Veículos" count={profile.vehicles.length}>
          {profile.vehicles.map((v, i) => (
            <Row key={i}>
              <div className="flex items-center gap-3">
                <LicensePlate plate={v.plate} />
                <div className="min-w-0 text-[14px]">
                  <div className="font-semibold">{v.model ?? "—"}</div>
                  <div className="text-slate-500 dark:text-slate-400">
                    {[v.year, v.color].filter(Boolean).join(" · ")}
                  </div>
                </div>
              </div>
            </Row>
          ))}
        </Collapsible>
      )}

      {covered("links") && (
        <Collapsible icon={Network} title="Vínculos" count={profile.links.length}>
          {profile.links.map((l, i) => (
            <Row key={i}>
              <div className="text-[15px] font-semibold">{l.name}</div>
              <div className="text-[13px] text-slate-500 dark:text-slate-400">
                {[l.relation, l.document].filter(Boolean).join(" · ")}
              </div>
            </Row>
          ))}
        </Collapsible>
      )}

      {publicData && (
        <Collapsible
          icon={Landmark}
          title="Dados públicos"
          count={publicData.appearsIn.length + publicData.companies.items.length}
        >
          {publicData.appearsIn.map((a) => (
            <Row key={a}>
              <div
                className={cn(
                  "text-[15px] font-semibold",
                  /CEIS|CNEP|CEAF/.test(a) ? "text-rose-700 dark:text-rose-300" : "",
                )}
              >
                Aparece em: {a}
              </div>
            </Row>
          ))}
          {publicData.companies.items.length > 0 && (
            <Row>
              <div className="text-[15px] font-semibold">
                {publicData.companies.items.length} empresa(s) na Receita Federal
              </div>
              <div className="text-[13px] text-slate-500 dark:text-slate-400">
                Detalhes completos no relatório em PDF.
              </div>
            </Row>
          )}
        </Collapsible>
      )}

      <p className="mt-6 flex items-start gap-2 px-1 text-[13px] text-slate-500 dark:text-slate-400">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        Todos os dados completos da fonte estão no relatório em PDF e na tela de Consultas do
        computador.
      </p>
    </>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[13px] text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-[16px] font-semibold leading-snug">{value}</dd>
    </div>
  );
}

function Row({ children }: { children: ReactNode }) {
  return <div className="border-t border-slate-100 px-4 py-3 first:border-t-0 dark:border-slate-800">{children}</div>;
}

function EmptyCard({ children }: { children: ReactNode }) {
  return (
    <Card className="px-4 py-5 text-center text-[15px] text-slate-500 dark:text-slate-400">{children}</Card>
  );
}

function Collapsible({
  icon: Icon,
  title,
  count,
  defaultOpen,
  children,
}: {
  icon: LucideIcon;
  title: string;
  count: number;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(!!defaultOpen && count > 0);
  return (
    <Card className="mt-3 overflow-hidden">
      <button
        type="button"
        onClick={() => count > 0 && setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-4 text-left"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <Icon className="h-5 w-5" />
        </span>
        <span className="flex-1 text-[17px] font-bold">{title}</span>
        <span
          className={cn(
            "min-w-8 rounded-full px-2.5 py-0.5 text-center text-[14px] font-bold",
            count
              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300"
              : "bg-slate-100 text-slate-500 dark:bg-slate-800",
          )}
        >
          {count}
        </span>
        {count > 0 && (
          <ChevronDown className={cn("h-5 w-5 text-slate-400 transition-transform", open && "rotate-180")} />
        )}
      </button>
      {open && <div className="border-t border-slate-100 dark:border-slate-800">{children}</div>}
    </Card>
  );
}
